import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@Pages/pageRuntime/components/ui/table/Table';
import type React from 'react';

type TableColumn<TRow> = {
  /**
   * Row field key. Used both as the React key and as the default value lookup.
   */
  key: Extract<keyof TRow, string>;

  /**
   * Column header content.
   */
  header: React.ReactNode;

  /**
   * Optional custom cell renderer.
   */
  render?: (row: TRow) => React.ReactNode;
};

type SimpleTableProps<TRow extends object> = {
  /**
   * Column definitions.
   */
  columns: TableColumn<TRow>[];

  /**
   * Rows to display.
   */
  rows: TRow[];

  /**
   * Text shown when `rows` is empty.
   */
  emptyText?: string;

  /**
   * Optional table caption.
   */
  caption?: string;

  className?: string;
};

/**
 * A simple static table for data already available in code. Use only for arrays already
 * available in code — standard live resource tables use `Sdk.Components.ResourceTable`.
 * Column keys and render rows are inferred from `rows`, so misspelled keys fail validation.
 *
 * @example
 * <Sdk.Components.SimpleTable columns={[{ key: 'name', header: 'Name' }]} rows={students} />
 */
export function SimpleTable<TRow extends object>({
  columns,
  rows,
  emptyText = 'No records found.',
  caption,
  className
}: SimpleTableProps<TRow>) {
  return (
    <Table className={className}>
      {caption ? <TableCaption>{caption}</TableCaption> : null}
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={column.key}>{column.header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length > 0 ? (
          rows.map((row, index) => (
            <TableRow key={String(('id' in row ? row.id : undefined) ?? index)}>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.render ? column.render(row) : String(row[column.key] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={columns.length || 1}
              className="py-8 text-center text-muted-foreground"
            >
              {emptyText}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
